import { useCallback, useRef, useState } from 'react';
import { runsService } from '../../services/runsService';
import { useFlowStore } from '../../store/flowStore';
import { ApiError } from '../../services/apiClient';
import { toast } from '../../store/uiStore';
import type { Flow, Run, RunEvent } from '../../types';

export interface RunState {
  running: boolean;
  events: RunEvent[];
  finishedRun: Run | null;
  pausedForHuman: boolean; // pausado em human_review (decisão vai para Minhas Atividades)
  pausedRun: Run | null;
  error: string | null;
}

const initial: RunState = {
  running: false,
  events: [],
  finishedRun: null,
  pausedForHuman: false,
  pausedRun: null,
  error: null,
};

// Dispara a execução REAL do fluxo no backend e revela os eventos passo a passo
// (destacando cada nó no canvas conforme o fluxo avança).
export function useFlowSimulation() {
  const [state, setState] = useState<RunState>(initial);
  const setNodeRunStatus = useFlowStore((s) => s.setNodeRunStatus);
  const resetRunStatus = useFlowStore((s) => s.resetRunStatus);
  const abortedRef = useRef(false);

  const start = useCallback(
    async (flow: Flow, input: string) => {
      abortedRef.current = false;
      resetRunStatus();
      setState({ ...initial, running: true });

      let run: Run;
      try {
        run = await runsService.execute(flow, input);
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'Falha ao executar o fluxo.';
        setState((s) => ({
          ...s,
          running: false,
          error: msg,
          events: [...s.events, { nodeId: flow.id, status: 'error', message: msg, timestamp: new Date().toISOString() }],
        }));
        toast.error(msg);
        return;
      }

      // Revela os eventos gradualmente para dar a sensação de "passo a passo".
      for (const e of run.events) {
        if (abortedRef.current) break;
        setNodeRunStatus(e.nodeId, { status: e.status, iteration: e.iteration });
        setState((s) => ({ ...s, events: [...s.events, e] }));
        await new Promise((r) => setTimeout(r, 300));
      }

      const paused = run.status === 'paused_for_human';
      setState((s) => ({
        ...s,
        running: false,
        finishedRun: paused ? null : run,
        pausedForHuman: paused,
        pausedRun: paused ? run : null,
      }));
    },
    [resetRunStatus, setNodeRunStatus],
  );

  const clear = useCallback(() => {
    resetRunStatus();
    setState(initial);
  }, [resetRunStatus]);

  const abort = useCallback(() => {
    abortedRef.current = true;
    setState((s) => ({ ...s, running: false }));
  }, []);

  return { state, start, clear, abort };
}
